import { useCallback, useRef, useState } from 'react'
import { useFocusEffect } from '@react-navigation/native'
import { useQueryClient } from '@tanstack/react-query'
import { EVENTS_QUERY_KEY } from './useEventQueries'

/**
 * Pull-to-refresh for the event screens: re-reads every event query (lists,
 * event page, attendees, discovery) and reports when it is done.
 */
export function useEventsPullToRefresh(): { refreshing: boolean; onRefresh: () => void } {
  const queryClient = useQueryClient()
  const [refreshing, setRefreshing] = useState(false)
  const onRefresh = useCallback(() => {
    setRefreshing(true)
    void queryClient
      .invalidateQueries({ queryKey: [EVENTS_QUERY_KEY] })
      .finally(() => setRefreshing(false))
  }, [queryClient])
  return { refreshing, onRefresh }
}

/**
 * Coming back to an event screen (after another screen, a payment, a link…)
 * re-reads the event data, so it never shows a stale phase or attendee list.
 * The first display is left to the queries themselves.
 */
export function useRefreshEventsOnFocus(): void {
  const queryClient = useQueryClient()
  const shown = useRef(false)
  useFocusEffect(
    useCallback(() => {
      if (!shown.current) {
        shown.current = true
        return
      }
      void queryClient.invalidateQueries({ queryKey: [EVENTS_QUERY_KEY] })
    }, [queryClient]),
  )
}
